import { XMark } from "@medusajs/icons";
import {
  AlertDialog as AlertDialogPrimitive,
  Dialog as DialogPrimitive,
  Popover as PopoverPrimitive,
  Tooltip as TooltipPrimitive,
} from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button, IconButton } from "./button";
import { Kbd } from "./primitives";

const overlay = [
  "fixed inset-0 z-50 bg-ui-bg-overlay",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
];

/* ---- Tooltip ------------------------------------------------------------- */

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  content,
  children,
  side = "top",
  delayDuration = 300,
  ...props
}: Omit<ComponentProps<typeof TooltipPrimitive.Content>, "content"> & {
  content: ReactNode;
  delayDuration?: number;
}) {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            "txt-compact-xsmall z-50 max-w-[240px] select-none rounded-md bg-ui-bg-component px-2.5 py-1 text-ui-fg-base shadow-elevation-tooltip",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
          {...props}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ---- Popover ------------------------------------------------------------- */

function PopoverContent({
  className,
  sideOffset = 8,
  collisionPadding = 8,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 w-72 rounded-lg bg-ui-bg-component p-3 text-ui-fg-base shadow-elevation-flyout outline-none",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverRoot(props: ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root {...props} />;
}

export const Popover = Object.assign(PopoverRoot, {
  Trigger: PopoverPrimitive.Trigger,
  Anchor: PopoverPrimitive.Anchor,
  Close: PopoverPrimitive.Close,
  Content: PopoverContent,
});

/* ---- Prompt: a confirmation that interrupts ------------------------------ */

function PromptContent({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className={cn(overlay)} />
      <AlertDialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-[400px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-ui-bg-base shadow-elevation-modal outline-none",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      />
    </AlertDialogPrimitive.Portal>
  );
}

function PromptHeader({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex flex-col gap-y-1 px-6 pt-6", className)} {...props} />;
}

function PromptTitle({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return <AlertDialogPrimitive.Title className={cn("h2-core", className)} {...props} />;
}

function PromptDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      className={cn("txt-compact-medium text-ui-fg-subtle", className)}
      {...props}
    />
  );
}

function PromptFooter({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex items-center justify-end gap-x-2 p-6", className)} {...props} />;
}

function PromptCancel({ children, ...props }: ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel asChild {...props}>
      <Button size="small" variant="secondary">
        {children}
      </Button>
    </AlertDialogPrimitive.Cancel>
  );
}

function PromptAction({
  children,
  tone = "danger",
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action> & { tone?: "danger" | "primary" }) {
  return (
    <AlertDialogPrimitive.Action asChild {...props}>
      <Button size="small" variant={tone}>
        {children}
      </Button>
    </AlertDialogPrimitive.Action>
  );
}

function PromptRoot(props: ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root {...props} />;
}

export const Prompt = Object.assign(PromptRoot, {
  Trigger: AlertDialogPrimitive.Trigger,
  Content: PromptContent,
  Header: PromptHeader,
  Title: PromptTitle,
  Description: PromptDescription,
  Footer: PromptFooter,
  Cancel: PromptCancel,
  Action: PromptAction,
});

/* ---- Drawer: edit something without leaving the screen ------------------- */

function DrawerContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={cn(overlay)} />
      <DialogPrimitive.Content
        className={cn(
          "fixed z-50 flex flex-col bg-ui-bg-base shadow-elevation-modal outline-none",
          // Phone: a sheet rising from the bottom. Larger screens: a panel from the right.
          "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl pb-safe",
          "sm:inset-x-auto sm:inset-y-2 sm:right-2 sm:max-h-none sm:w-full sm:max-w-[560px] sm:rounded-lg sm:pb-0",
          "data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out-expo",
          "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
          "sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0",
          "sm:data-[state=open]:slide-in-from-right-1/2 sm:data-[state=closed]:slide-out-to-right-1/2",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

function DrawerHeader({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-x-4 border-b border-ui-border-base px-6 py-4",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col gap-y-1">{children}</div>
      <div className="flex items-center gap-x-2">
        <Kbd className="hidden sm:inline-flex">esc</Kbd>
        <DialogPrimitive.Close asChild>
          <IconButton aria-label="Close" size="small" variant="transparent">
            <XMark />
          </IconButton>
        </DialogPrimitive.Close>
      </div>
    </div>
  );
}

function DrawerTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("h1-core", className)} {...props} />;
}

function DrawerDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("txt-small text-ui-fg-subtle", className)}
      {...props}
    />
  );
}

function DrawerBody({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex-1 overflow-y-auto px-6 py-4", className)} {...props} />;
}

function DrawerFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-x-2 border-t border-ui-border-base px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

// Each compound component gets its own root function. Assigning onto the shared
// Radix Root object would make Drawer and FocusModal the same thing.
function DrawerRoot(props: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

export const Drawer = Object.assign(DrawerRoot, {
  Trigger: DialogPrimitive.Trigger,
  Close: DialogPrimitive.Close,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Body: DrawerBody,
  Footer: DrawerFooter,
});

/* ---- FocusModal: a full-screen task, such as creating a Project ---------- */

function FocusModalContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={cn(overlay)} />
      <DialogPrimitive.Content
        className={cn(
          "fixed inset-0 z-50 flex flex-col overflow-hidden bg-ui-bg-base shadow-elevation-modal outline-none sm:inset-2 sm:rounded-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=open]:slide-in-from-bottom-2",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.98] data-[state=closed]:slide-out-to-bottom-2",
          "duration-200",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

function FocusModalHeader({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "pt-safe flex items-center justify-between gap-x-4 border-b border-ui-border-base px-4 py-2",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-x-2">
        <DialogPrimitive.Close asChild>
          <IconButton aria-label="Close" size="small" variant="transparent">
            <XMark />
          </IconButton>
        </DialogPrimitive.Close>
        <Kbd className="hidden sm:inline-flex">esc</Kbd>
      </div>
      <div className="flex items-center gap-x-2">{children}</div>
    </div>
  );
}

function FocusModalBody({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex-1 overflow-y-auto", className)} {...props} />;
}

function FocusModalRoot(props: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

export const FocusModal = Object.assign(FocusModalRoot, {
  Trigger: DialogPrimitive.Trigger,
  Close: DialogPrimitive.Close,
  Content: FocusModalContent,
  Header: FocusModalHeader,
  Title: DialogPrimitive.Title,
  Description: DialogPrimitive.Description,
  Body: FocusModalBody,
});
